import { Show, createEffect, createSignal } from "solid-js";
import type { SuggestedQuestion } from "../../types/suggestquestions";
import type { PriorityTag } from "../../types/caseState";
import SuggestionsPanel from "../SuggestionsPanel";
import AnsweredPanel from "../AnsweredPanel";
import { translateBatch } from "../../api/translateBatch";
import { uiHash, uiLog } from "../../utils/telemetry";
import { PanelHeader } from "../PanelHeader";

const ScreeningPanel = (props: {
  likelihood: number | undefined;
  contactType: string;
  aiExplanation?: string;
  alerts?: string[];
  screeningDecision: string | undefined;
  maltreatment: string | undefined;
  priorityRanking: number | undefined;
  llmPredictionExplanation: string | undefined;
  offenderRole: string | undefined;
  victimType: string | undefined;
}) => (
  <div class="card">
    <div class="flex items-baseline gap-2 mb-0.5">
      <span>📄</span>
      <PanelHeader title="Screening Recommendation: " />

      <span class="text-[11px] text-gray-500 font-medium shrink-0 whitespace-nowrap ml-1">
       <Show when={props.screeningDecision === "Screen In"}>
        <span class="text-green-500 font-bold">Screened In</span>
       </Show>
       <Show when={props.screeningDecision === "Screen Out"}>
        <span class="text-red-500 font-bold">Screened Out</span>
       </Show>
      </span>
      
    </div>
    <div class="mt-2 text-sm text-gray-600">
      
      <Show
        when={props.likelihood === undefined}
        fallback={<p class="font-bold text-black  text-lg"> Likelihood Score: {Math.round((props.likelihood || 0)*100)}%</p>}
      >
        <p>Waiting for session finalization to calculate likelihood...</p>
      </Show>
     
    </div>
    <span class="font-bold text-lg">Priority: {props.priorityRanking}</span>
    

    <Show when={props.aiExplanation}>
      <div class="mt-4">
        <h4 class="text-lg font-bold text-gray-700 mb-1">AI Explanation</h4>
        <p class="text-sm text-gray-600 leading-relaxed">
          {props.aiExplanation || "Placeholder: AI-generated explanation of the screening results will appear here."}
        </p>
      </div>
    </Show>

    <Show when={props.maltreatment}>
      <div class="mt-4">
        <h4 class="text-lg font-bold text-gray-700 mb-1">Maltreatment Type</h4>
        <p class="text-sm text-gray-600 leading-relaxed">
          {props.maltreatment || "Placeholder: Maltreatment type explanation of the screening results will appear here."}
        </p>
      </div>
    </Show>

    <Show when={props.offenderRole}>
      <div class="mt-4">
        <h4 class="text-lg font-bold text-gray-700 mb-1">Offender Role</h4>
        <p class="text-sm text-gray-600 leading-relaxed">
          {props.offenderRole || "Placeholder: Offender role explanation of the screening results will appear here."}
        </p>
      </div>
    </Show>

    <Show when={props.victimType}>
      <div class="mt-4">
        <h4 class="text-lg font-bold text-gray-700 mb-1">Victim Type</h4>
        <p class="text-sm text-gray-600 leading-relaxed">
          {props.victimType || "Placeholder: Victim type explanation of the screening results will appear here."}
        </p>
      </div>
    </Show>

    <Show when={(props.alerts && props.alerts.length > 0) }>
      <div class="mt-4">
        <h4 class="text-lg font-bold text-gray-700 mb-1">Alerts</h4>
        <div class="flex flex-wrap gap-2">
          <Show 
            when={props.alerts && props.alerts.length > 0} 
            fallback={
              <span class="px-2 py-1 rounded-md bg-gray-50 border border-gray-200 text-gray-500 text-xs font-medium">
                No active alarms
              </span>
            }
          >
            {props.alerts?.map((alert) => (
              <span class="px-2 py-1 rounded-md bg-red-50 border border-red-200 text-red-700 text-xs font-medium">
                {alert}
              </span>
            ))}
          </Show>
        </div>
      </div>
    </Show>
    {/* <div class="flex flex-wrap mt-4">
    <h4 class="text-lg font-bold text-gray-700 mb-1">Maltreatment Type</h4>
    </div> */}
  </div>
);

export default function InterviewTab(props: {
  screeningDecision: string | undefined;
  maltreatment: string | undefined;
  llmPredictionExplanation: string | undefined;
  priorityRanking: number | undefined;
  alerts: string[];
  offenderRole: string | undefined;
  victimType: string | undefined;
  sessionId: string | null;
  interpreterEnabled: boolean;
  suggestions: SuggestedQuestion[];
  asked: string[];
  dismissed: any[];
  earlier: SuggestedQuestion[];
  contactType: string;
  readOnly: boolean;
  likelihood: number | undefined;
  caseState: any;
  setCaseState: (fn: (prev: any) => any) => void;

  onAsk: (question: string, opts?: any) => void;
  onDismiss: (q: SuggestedQuestion) => void;
  onRestore: (q: SuggestedQuestion) => void;
  onDismissEarlier: (q: SuggestedQuestion) => void;

  removeFromSuggestions: (question: string) => void;
  addBackToSuggestions: (
    q: SuggestedQuestion & { priority_level?: PriorityTag },
  ) => void;
  setEarlierFromPersisted: (next: SuggestedQuestion[]) => void;
  persistInterviewMetadata?: (extra?: any) => void;
}) {
  const [likelihood, setLikelihood] = createSignal<number | undefined>(undefined);
  const [aiExplanation, setAiExplanation] = createSignal<string | undefined>(props.llmPredictionExplanation);
  const [alerts, setAlerts] = createSignal<string[]>(props.alerts || []);
  const [priorityRanking, setPriorityRanking] = createSignal<number | undefined>(props.priorityRanking);
  const [screeningDecision, setScreeningDecision] = createSignal<string | undefined>(props.screeningDecision);
  const [maltreatment, setMaltreatment] = createSignal<string | undefined>(props.maltreatment);
  const [llmPredictionExplanation, setLlmPredictionExplanation] = createSignal<string | undefined>(props.llmPredictionExplanation);
  const [offenderRole, setOffenderRole] = createSignal<string | undefined>(props.offenderRole);
  const [victimType, setVictimType] = createSignal<string | undefined>(props.victimType);

  createEffect(() => {
    setLikelihood(props.likelihood);
    setAiExplanation(props.llmPredictionExplanation);
    setAlerts(props.alerts);
    setPriorityRanking(props.priorityRanking);
    setScreeningDecision(props.screeningDecision);
    setMaltreatment(props.maltreatment);
    setLlmPredictionExplanation(props.llmPredictionExplanation);
    setOffenderRole(props.offenderRole);
    setVictimType(props.victimType);
  });
  return (
    <div class="grid grid-cols-1 lg:grid-cols-5 gap-1 items-start">
      <SuggestionsPanel
        suggestions={props.suggestions}
        asked={props.asked}
        dismissed={props.dismissed as any}
        likelihood={likelihood()}
        onDismiss={props.onDismiss}
        onRestore={(q) => {
          try {
            props.onRestore(q);

            const norm = (s: string) => (s || "").trim().toLowerCase();
            const cur = (props.earlier || []).slice();
            const exists = new Set(cur.map((e) => norm(e.question)));
            let next = cur;
            if (!exists.has(norm(q.question))) {
              next = [...cur, { ...q, dismissed: false } as any];
              props.setEarlierFromPersisted(next);
            }

            try {
              props.removeFromSuggestions(q.question);
            } catch {}

            try {
              props.persistInterviewMetadata?.({ earlierSuggestions: next });
            } catch {}
          } catch (e) {
            console.error("Failed to restore question:", e);
          }
        }}
        onDismissEarlier={(q) => {
          try {
            props.onDismissEarlier(q);
          } catch {}
        }}
        earlier={props.earlier}
        onAsk={(q) => props.onAsk(q.question)}
        contactType={props.contactType}
        readOnly={props.readOnly}
        onReturnDrop={(payload, toBucket) => {
          const question = payload?.question as string;
          const fromBucket =
            payload?.priority_level ??
            props.caseState.answeredQuestions.find(
              (qa: any) => qa.question === question,
            )?.priority_level;

          if (toBucket && fromBucket && toBucket !== fromBucket) {
            const ok = window.confirm(
              `Move ${question} from ${fromBucket} ${toBucket}?`,
            );
            if (!ok) return;
          }

          props.setCaseState((prev) => ({
            ...prev,
            answeredQuestions: prev.answeredQuestions.filter(
              (qa: any) => qa.question !== question,
            ),
            askedQuestions: prev.askedQuestions.filter(
              (q: string) => q !== question,
            ),
          }));

          props.addBackToSuggestions({
            id: question,
            question,
            priority_level: toBucket || fromBucket,
          } as any);
          props.persistInterviewMetadata?.();
        }}
      />
       <ScreeningPanel
        likelihood={likelihood()}
        contactType={props.contactType}
        aiExplanation={aiExplanation()}
        screeningDecision={screeningDecision()}
        maltreatment={maltreatment()}
        priorityRanking={priorityRanking()}
        llmPredictionExplanation={llmPredictionExplanation()}
        offenderRole={offenderRole()}
        victimType={victimType()}
        alerts={alerts()}
      /> 

      <AnsweredPanel
        answered={props.caseState.answeredQuestions as any}
        readOnly={props.readOnly}
        onConfirmInferred={(question) => {
          props.setCaseState((prev) => ({
            ...prev,
            answeredQuestions: prev.answeredQuestions.map((qa: any) =>
              qa.question === question
                ? {
                    ...qa,
                    answerStatus: "answered",
                    answeredAt: qa.answeredAt || new Date().toISOString(),
                  }
                : qa,
            ),
          }));
          props.persistInterviewMetadata?.();
        }}
        onUpdate={(original, changes) => {
          const questionKey =
            changes.question && changes.question !== original
              ? changes.question
              : original;
          const answerText =
            typeof changes.answer === "string" ? changes.answer.trim() : "";
          const hasAnswer = !!answerText;

          // Helper to update a single answered question by key
          const updateAnsweredQuestion = (
            list: any[],
            key: string,
            updates: Record<string, any>,
          ) =>
            list.map((qa: any) =>
              qa.question === key ? { ...qa, ...updates } : qa,
            );

          // Base update: apply changes + set status/timestamp
          props.setCaseState((prev) => {
            const qChanged = changes.question && changes.question !== original;
            return {
              ...prev,
              answeredQuestions: updateAnsweredQuestion(
                prev.answeredQuestions,
                original,
                {
                  ...changes,
                  answerStatus: hasAnswer
                    ? "answered"
                    : typeof changes.answer === "string"
                      ? "no_answer"
                      : undefined,
                  answeredAt: hasAnswer
                    ? prev.answeredQuestions.find(
                        (q: any) => q.question === original,
                      )?.answeredAt || new Date().toISOString()
                    : undefined,
                },
              ),
              askedQuestions: qChanged
                ? prev.askedQuestions.map((q: string) =>
                    q === original ? changes.question! : q,
                  )
                : prev.askedQuestions,
            };
          });
          props.persistInterviewMetadata?.();

          // Translation: single path for both interpreter and non-interpreter
          if (hasAnswer) {
            const translateAndUpdate = async () => {
              const answer_en = props.interpreterEnabled
                ? (
                    await translateBatch([answerText], "en-US").catch(() => [
                      answerText,
                    ])
                  )[0]
                : answerText;
              props.setCaseState((prev) => ({
                ...prev,
                answeredQuestions: updateAnsweredQuestion(
                  prev.answeredQuestions,
                  questionKey,
                  {
                    answer_en,
                    answerStatus: "answered",
                    answeredAt:
                      prev.answeredQuestions.find(
                        (q: any) => q.question === questionKey,
                      )?.answeredAt || new Date().toISOString(),
                  },
                ),
              }));
              props.persistInterviewMetadata?.();
            };
            translateAndUpdate();
          }
        }}
        onRemove={(question) => {
          props.setCaseState((prev) => ({
            ...prev,
            answeredQuestions: prev.answeredQuestions.filter(
              (qa: any) => qa.question !== question,
            ),
          }));
          props.persistInterviewMetadata?.();
        }}
        onAskDrop={(payload) => {
          const q = typeof payload === "string" ? payload : payload.question;
          const priority_level =
            typeof payload === "string" ? undefined : payload.priority_level;
          const fieldKey =
            typeof payload === "string" ? undefined : payload.fieldKey;
          const origin =
            typeof payload === "string" ? undefined : payload.origin;

          uiLog("asked_manual_drop_handler", {
            sessionId: props.sessionId,
            question_sha: uiHash(q || ""),
            priority_level,
          });

          const alreadyAsked = props.caseState.askedQuestions.includes(q);
          const alreadyAnswered = props.caseState.answeredQuestions.some(
            (qa: any) => qa.question === q,
          );
          if (alreadyAsked || alreadyAnswered) return;

          try {
            props.onAsk(q, {
              source: "manual",
              origin,
              priority_level,
              fieldKey,
            });
            props.removeFromSuggestions(q);
          } catch (err) {
            console.error("Failed to mark question as asked:", err);
          }
        }}
      />

      <Show
        when={new URLSearchParams(window.location.search).get("dev") === "1"}
      >
        <div class="lg:col-span-4 border rounded p-2 bg-yellow-50 text-xs mt-2">
          <div class="font-semibold mb-1">Debug Case Fields (live)</div>
          <pre class="whitespace-pre-wrap break-words">
            {JSON.stringify(
              {
                contactType: props.caseState.contactType,
                ...(props.caseState as any),
              },
              null,
              2,
            )}
          </pre>
        </div>
      </Show>
    </div>
  );
}
